import { motion } from "framer-motion";
import { BookOpen } from "lucide-react";
import { BOOK_FRONT_URL } from "@/lib/constants";
import { cn } from "@/lib/utils";

const SIZES = {
  sm: { container: "max-w-[220px]" },
  md: { container: "max-w-xs" },
  lg: { container: "max-w-sm sm:max-w-md" },
};

/**
 * Real published book cover — "The Adventure of Moya and Kotzinon"
 * by Uri Menashe Eini, © 2018, Sifrei Niv (Niv Books).
 * Renders a cinematic frame around the actual scanned cover.
 */
export default function StorybookCover({ size = "md", className, showRibbon = true, ribbonText = "FIRST BOOK" }) {
  const s = SIZES[size] || SIZES.md;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={cn("relative w-full", s.container, className)}
      data-testid="storybook-cover"
    >
      {/* Ambient glow behind the book */}
      <div className="absolute -inset-8 bg-[radial-gradient(circle_at_center,hsla(43,92%,56%,0.28),transparent_65%)] blur-2xl pointer-events-none" />

      {/* Book spine shadow */}
      <div className="absolute -left-1.5 top-3 bottom-3 w-1.5 rounded-l-md bg-gradient-to-b from-[#5a3210] via-[#2a1607] to-[#5a3210] shadow-inner" />

      {/* Book body */}
      <div
        className={cn(
          "relative rounded-r-2xl rounded-l-md border border-[hsl(var(--kotz-gold))]/35 overflow-hidden aspect-[2/3]",
          "shadow-[0_50px_120px_-30px_rgba(0,0,0,0.9)]",
          "bg-black"
        )}
      >
        <img
          src={BOOK_FRONT_URL}
          alt="The Adventure of Moya and Kotzinon — book cover by Uri Menashe Eini"
          className="w-full h-full object-cover object-center block"
          loading="lazy"
        />

        {/* Soft cinematic vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

        {/* Gold accent frame */}
        <div className="absolute inset-2 border border-[hsl(var(--kotz-gold))]/30 rounded-md pointer-events-none" />

        {/* Bottom badge */}
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em] text-white/85">
          <span className="inline-flex items-center gap-1.5">
            <BookOpen className="h-3 w-3 text-[hsl(var(--kotz-gold))]" />
            First Book
          </span>
          <span className="text-[hsl(var(--kotz-gold))]">© 2018</span>
        </div>
      </div>

      {/* Corner ribbon (optional) */}
      {showRibbon && (
        <div className="absolute -top-3 -right-3 rotate-[10deg] z-10">
          <div className="bg-[hsl(var(--kotz-red))] text-white px-3 py-1 rounded-md shadow-lg font-display text-[11px] tracking-[0.2em] border-2 border-white/20">
            {ribbonText}
          </div>
        </div>
      )}
    </motion.div>
  );
}
